import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  CircularProgress,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";

// Shared form & hooks
import CreateAutomationForm from "../forms/CreateAutomationForm";
import useAutomations from "../hooks/automationhooks/useAutomations";

/**
 * Utilities ─────────────────────────────────────────────
 */
const parseJsonSafe = (str, fallback = {}) => {
  if (str === null || str === undefined || str === "") return fallback;
  try {
    return JSON.parse(str);
  } catch {
    return fallback;
  }
};

const toPlainObject = (value, fallback = {}) => {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value === "object") return value; // already parsed
  return parseJsonSafe(value, fallback);
};

/**
 * EditAutomationPage
 *
 * UI & layout intentionally mirror CreateAutomationPage.jsx so styling is
 * identical. Behaviour switches from "create" to "update" when an
 * automationId param exists.
 *
 * Route: /journeys/:brandId/automation/:automationId/edit
 */
export default function EditAutomationPage() {
  const { brandId, automationId } = useParams();
  const navigate = useNavigate();

  const {
    handleGetUserData, // still needed for other screens → keeps hook contract
    handleGetJourneyAutomationById,
    handleEditAutomation,
  } = useAutomations();

  const [automationData, setAutomationData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  /* ── bootstrap ─────────────────────────────────────── */
  useEffect(() => {
    // Pre‑load user context so downstream components (headers, etc.) work.
    handleGetUserData?.();
  }, []);

  /* ── fetch the target automation ───────────────────── */
  useEffect(() => {
    const fetchAutomation = async () => {
      setIsLoading(true);
      try {
        const autResponse = await handleGetJourneyAutomationById(automationId);
        const aut = autResponse[0];

        // server can send raw strings *or* objects → normalise
        const scheduleRaw = toPlainObject(aut.Schedule ?? aut.schedule);
        const metrics = toPlainObject(aut.Metrics ?? aut.metrics);
        const audiences = toPlainObject(aut.Audiences ?? aut.audiences, []);
        const settings = toPlainObject(aut.Settings ?? aut.settings);

        let start = scheduleRaw.Start ? dayjs(scheduleRaw.Start) : null;
        let end = scheduleRaw.End ? dayjs(scheduleRaw.End) : null;
        if (end && end.isSame(dayjs("1970-01-01T00:00:00.000Z"))) end = null;

        setAutomationData({
          id: aut.Id,
          brandId: aut.BrandId,
          userId: aut.UserId,
          name: aut.Name,
          description: aut.Description,
          priority: aut.Priority,
          status: aut.Status,
          schedule: { start, end },
          metrics,
          audiences,
          settings,
        });
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    if (automationId) fetchAutomation();
  }, [automationId]);

  /* ── validation ─────────────────────────────────────── */
  const isFormValid = () => {
    if (!automationData) return false;

    const { name, status, priority, schedule } = automationData;
    if (!name || !status || !priority || !schedule?.start) return false;

    const start = dayjs(schedule.start);
    const end = schedule.end ? dayjs(schedule.end) : null;

    if (!start.isValid() || start.isBefore(dayjs())) return false;
    if (end && (!end.isValid() || !start.isBefore(end))) return false;

    return true;
  };

  /* ── save ───────────────────────────────────────────── */
  const handleSave = async () => {
    if (!isFormValid()) return;
    setIsSaving(true);
    try {
      const dataForUpdate = {
        name: automationData.name,
        description: automationData.description,
        priority: automationData.priority,
        status: automationData.status,
        schedule: {
          start: automationData.schedule.start,
          end: automationData.schedule.end,
        },
        metrics: automationData.metrics,
        audiences: automationData.audiences,
        settings: automationData.settings,
      };

      await handleEditAutomation(
        automationData.id,
        dataForUpdate,
        automationData.brandId,
        automationData.userId
      );

      window.location.replace(
        `https://icreate-campaign.com/admin/Campaigns/Index?app=1004&brand=${brandId}`
      );
    } catch (error) {
      console.error("Error saving automation:", error);
      alert(`Failed to save: ${error.message || "Unknown error"}`);
    } finally {
      setIsSaving(false);
    }
  };

  /* ── render ─────────────────────────────────────────── */
  if (isLoading || !automationData) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
      <Paper elevation={3} sx={{ width: 640, p: 4 }}>
        <Typography variant="h4" fontWeight="bold" mb={2}>
          Edit Journey
        </Typography>

        <CreateAutomationForm
          automationData={automationData}
          setAutomationData={setAutomationData}
        />

        <Stack direction="row" spacing={2} justifyContent="flex-end" mt={3}>
          <Button
            onClick={() =>
              (window.location.href = `https://icreate-campaign.com/admin/Campaigns/Index?app=1004&brand=${brandId}`)
            }
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!isFormValid() || isSaving}
            onClick={handleSave}
          >
            {isSaving ? "Saving…" : "Save"}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
